/** Offer again is a Task in place. No sheet, dialog or automatic navigation. */
import { useTabDrafts } from "@/components/ui/box-draft";
import type { Blocker, Read, TaskBox } from "@/components/ui/box.schema";
import type { Semester } from "@/lib/api/academicStructure";
import { sectionsAlreadyInTerm, type Course, type SectionInTerm } from "@/lib/api/courses";
import { registrarCoursePath } from "@/navigation/routes";
import { sectionCommands } from "./section-commands";
import { useSectionTaskResults, type SectionPending } from "./section-task-result";
import type { SectionSave } from "./use-section-save";
import { registrarCourseCopy as words } from "./registrar-course-copy";

type OfferDraft = { term: string; existing: SectionInTerm[] };
export function useOfferAgainModel({ course, recordId, read, semesters, engine, choicesReady }: { course: Course | null; recordId: string; read: Read<Course>; semesters: Semester[]; engine: SectionSave; choicesReady: boolean }): TaskBox<Course> {
  const drafts = useTabDrafts<OfferDraft, SectionPending>("offer-course-again");
  const draft = drafts({ record: recordId, version: course?.updated_at ?? null, initial: { term: "", existing: [] } });
  const results = useSectionTaskResults();
  const id = `offer-${recordId}`;
  const terms = semesters.filter(term => term.status !== "closed" && term.id !== course?.semester_id).sort((a, b) => (a.start_date ?? "9999").localeCompare(b.start_date ?? "9999") || a.name.localeCompare(b.name));
  const selected = terms.find(term => term.id === draft.value.term);
  const duplicate = draft.value.existing.length > 0;
  const effect = words.offerEffect(course?.code ?? words.section, selected?.name ?? words.chooseTerm, duplicate);
  const result = results.result(id, draft);
  const created = results.created[id];
  return {
    id, kind: "task", job: words.offerJob, label: words.offer, owner: { system: "imam" }, read: draft.settled ? read : { status: "loading" }, fold: "choice",
    state: () => ({ class: "quiet", word: words.notOffered }), detail: () => words.offerDetail,
    act: result.state === "unknown" ? { does: "check", verb: words.check } : result.state === "confirmed" ? undefined : { does: "open", verb: words.offer },
    exits: created ? [{ label: words.openDraft, to: registrarCoursePath(created) }] : undefined,
    draft: draft.kept,
    parts: [{ id: "term", label: words.term, owner: { system: "imam" }, state: { class: draft.value.term ? "draft" : "waiting", word: selected?.name ?? words.chooseTerm }, fold: "never", draft: "browser", keep: draft,
      conflict: draft.conflict && course ? { sentence: words.newerSection, facts: [{ label: words.section, theirs: `${course.code} · ${course.title}` }], keepMine: { label: words.keepMine, run: draft.keepMine }, loadNewer: { label: words.loadNewer, run: draft.loadNewer } } : undefined,
      render: () => <>
        <label>{words.term}<select aria-label={words.offerTerm} disabled={!choicesReady || !!draft.pending} value={draft.value.term} onChange={event => draft.set({ term: event.target.value, existing: [] })}><option value="">{words.chooseTerm}</option>{terms.map(term => <option key={term.id} value={term.id}>{term.name}</option>)}</select></label>
        {choicesReady && terms.length === 0 && <p>{words.noOtherTerm}</p>}
        <p>{words.offerCopied}</p><p>{words.offerNotCopied}</p>
        {duplicate && <section aria-label={words.existingSections}><p>{words.existingWarning}</p><ul>{draft.value.existing.map(section => <li key={section.id}>{section.code} · {section.title} · {section.crn ?? section.status}</li>)}</ul></section>}
      </> }],
    commit: {
      verb: duplicate ? words.addAnother : words.createDraft, cap: words.whenOffer,
      effects: () => [{ id: "draft", sentence: effect }],
      blockers: (): Blocker[] => [
        ...(!course || !choicesReady || !selected ? [{ kind: "missing" as const, part: "term", sentence: words.chooseOpenTerm }] : []),
        ...(engine.hasUnsaved || engine.busy ? [{ kind: "missing" as const, part: "term", sentence: words.saveFactsFirst }] : []),
        ...(draft.conflict ? [{ kind: "conflict" as const, part: "term", sentence: words.newerSection }] : []),
      ],
      run: key => results.run(id, draft, { key, action: "course.offer", course: recordId, effects: [effect], remains: [words.offerNotCopied] }, async markSent => {
        const current = await engine.review();
        if (current.updated_at !== draft.began) throw new Error(words.newerSection);
        try {
          markSent();
          const saved = await sectionCommands.offer(current, draft.value.term, duplicate, key);
          return { created_id: saved.id };
        } catch (error) {
          const existing = sectionsAlreadyInTerm(error);
          if (existing) draft.set(was => ({ ...was, existing }));
          throw error;
        }
      }),
    }, result,
  };
}
